import React from 'react';
import { Button } from '@mui/material';
import { ORDER_TYPE_STATUS_FLOW } from '../../../types/statusFlows';
import { ProcedureStatus } from '../../../types/procedures';

interface OrderActionsProps {
  orderType: string;
  status: string;
  isImagingOrderValid: boolean;
  onSaveDraft: () => void;
  onSignAndSend: () => void;
  onSendToLab: () => void;
  onResendLab: () => void;
  onResendImaging: () => void;
  onPrintRequisition: () => void;
  onPrintReferral: () => void;
  onCreateCheckupOrders: () => void;
}

export const OrderActions: React.FC<OrderActionsProps> = ({
  orderType,
  status,
  isImagingOrderValid,
  onSaveDraft,
  onSignAndSend,
  onSendToLab,
  onResendLab,
  onResendImaging,
  onPrintRequisition,
  onPrintReferral,
  onCreateCheckupOrders,
}) => {
  // Hide all action buttons if status is Cancelled or Completed (using enum)
  if (status === ProcedureStatus.CANCELLED || status === ProcedureStatus.COMPLETED) {
    return null;
  }

  const showSaveDraft = !status || status === ProcedureStatus.SAVED_AS_DRAFT;
  
  // Determine which print buttons to show based on order type
  const showPrintRequisition = ['Laboratory Test', 'Imaging'].includes(orderType);
  const showPrintReferral = ['Physical Exam', 'Intervention / Risk Assessment', 'Generic Procedure', 'Intervention', 'Procedure'].includes(orderType);

  if (orderType === 'Laboratory Test') {
    if (status === (ORDER_TYPE_STATUS_FLOW['Laboratory Test']?.[2] || ProcedureStatus.ORDERED)) {
      return (
        <>
          <Button variant="contained" color="secondary" onClick={onResendLab}>
            Resend to Lab
          </Button>
          <Button onClick={onPrintRequisition}>Print Requisition Form</Button>
        </>
      );
    }
    return (
      <>
        {showSaveDraft && <Button onClick={onSaveDraft}>Save as Draft</Button>}
        <Button variant="contained" onClick={onSignAndSend}>Sign & Send to Lab</Button>
        <Button onClick={onPrintRequisition}>Print Requisition Form</Button>
      </>
    );
  }

  if (orderType === 'Imaging') {
    if (status === (ORDER_TYPE_STATUS_FLOW['Imaging']?.[2] || ProcedureStatus.ORDERED)) {
      return (
        <>
          <Button variant="contained" color="secondary" onClick={onResendImaging}>
            Resend to Imaging Lab
          </Button>
          <Button onClick={onPrintRequisition}>Print Requisition Form</Button>
        </>
      );
    }
    return (
      <>
        {showSaveDraft && <Button onClick={onSaveDraft}>Save as Draft</Button>}
        <Button
          variant="contained"
          onClick={onSendToLab}
          disabled={!isImagingOrderValid}
        >
          Send to Imaging Center
        </Button>
        <Button onClick={onPrintRequisition}>Print Requisition Form</Button>
      </>
    );
  }

  if (orderType === 'Referral') {
    return (
      <>
        {showSaveDraft && <Button onClick={onSaveDraft}>Save as Draft</Button>}
        <Button variant="contained" onClick={onSignAndSend}>Sign Order</Button>
        <Button onClick={onPrintReferral}>Print Referral Form</Button>
      </>
    );
  }

  if (orderType === 'Physical Exam' || orderType === 'Intervention / Risk Assessment' || orderType === 'Generic Procedure' || orderType === 'Procedure' || orderType === 'Intervention') {
    return (
      <>
        {showSaveDraft && <Button onClick={onSaveDraft}>Save as Draft</Button>}
        <Button variant="contained" onClick={onSignAndSend}>Sign Order</Button>
        <Button onClick={onPrintReferral}>Print Referral Form</Button>
      </>
    );
  }

  if (orderType === 'Encounter Checkup Procedure') {
    return (
      <>
        <Button variant="contained" onClick={onCreateCheckupOrders}>
          Create Checkup Orders
        </Button>
        <Button onClick={onPrintReferral}>Print Referral Form</Button>
      </>
    );
  }

  // Default case for any other order types
  if (orderType) {
    return (
      <>
        {showSaveDraft && <Button onClick={onSaveDraft}>Save as Draft</Button>}
        <Button variant="contained" onClick={onSignAndSend}>Sign Order</Button>
        {showPrintRequisition && <Button onClick={onPrintRequisition}>Print Requisition Form</Button>}
        {showPrintReferral && <Button onClick={onPrintReferral}>Print Referral Form</Button>}
      </>
    );
  }

  // Show Save as Draft even when no order type is selected
  return (
    <>
      {showSaveDraft && <Button onClick={onSaveDraft}>Save as Draft</Button>}
    </>
  );
}; 